import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AnalyticsService } from '@shared/service-analytics';
import { LOGGER_CONFIG } from './logger.config';
import { LogLevel } from './logger.types';

@Injectable({ providedIn: 'root' })
export class LoggerService {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly config = inject(LOGGER_CONFIG);

  private getConsoleMethod(level: LogLevel): typeof console.log {
    switch (level) {
      case 'ERROR':
        return console.error;
      case 'WARN':
        return console.warn;
      default:
        return console.log;
    }
  }

  private log(level: LogLevel, message: string, source?: string, ...args: unknown[]): void {
    if (!this.config.activeLogLevels.has(level)) {
      return;
    }

    const timestamp = new Date().toISOString();
    const sourcePrefix = source ? `[${source}] ` : '';
    const formattedMessage = `${timestamp} ${sourcePrefix}${message}`;

    const consoleMethod = this.getConsoleMethod(level);
    consoleMethod(formattedMessage, ...args);
  }

  debug(message: string, source?: string, ...args: unknown[]): void {
    this.log('DEBUG', message, source, ...args);
  }

  info(message: string, source?: string, ...args: unknown[]): void {
    this.log('INFO', message, source, ...args);
  }

  warn(message: string, source?: string, ...args: unknown[]): void {
    this.log('WARN', message, source, ...args);
  }

  error(message: string, source?: string, ...args: unknown[]): void {
    this.log('ERROR', message, source, ...args);
    this.captureErrorToPostHog(message, source, args);
  }

  private captureErrorToPostHog(message: string, source: string | undefined, args: unknown[]): void {
    const errorArg = args.find(
      (arg): arg is Error | HttpErrorResponse => arg instanceof Error || arg instanceof HttpErrorResponse,
    );
    if (!errorArg) {
      return;
    }

    const properties: Record<string, unknown> = {
      message,
      source,
    };

    if (errorArg instanceof HttpErrorResponse) {
      properties['status'] = errorArg.status;
      properties['statusText'] = errorArg.statusText;
      properties['url'] = errorArg.url;
    }

    this.analyticsService.captureException(errorArg, properties);
  }
}
